import { Button, Flex, HStack, Image, Stack, Tag, Text, Tooltip } from '@chakra-ui/react';
import { isTestingEnvironment } from 'helpers/environments';
import { get } from 'lodash';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { IdentificationResultIcon } from '../../components/Icons';
import { dateTimeTransformer } from '../../helpers/dateTransformer';
import { displayRow } from '../../helpers/general';
import useWorkshopQuery from '../../hooks/useWorkshopQuery';
import { ContentCard } from '../../layout/ContentCard';

const WorkshopDetailsCard = ({ workshopId }: { workshopId: number }) => {
  const { t } = useTranslation();
  const workshopQuery = useWorkshopQuery(workshopId);

  const salesforceAccountId = (workshopQuery?.data as any)?.salesforce_lead_id
    ? (workshopQuery?.data as any)?.salesforce_lead_id
    : ((workshopQuery?.data as any)?.salesforce_account_id ?? null);
  const isAppOnTestingEnvironment = isTestingEnvironment();
  const isDeletionPending = workshopQuery.data?.status === 600;

  return (
    <ContentCard
      isLoading={workshopQuery.isFetching}
      icon={
        salesforceAccountId ? (
          <a
            href={
              isAppOnTestingEnvironment
                ? `https://oeservice--chargebee.sandbox.lightning.force.com/lightning/r/Account/${salesforceAccountId}/view`
                : `https://oeservice.lightning.force.com/lightning/r/Account/${salesforceAccountId}/view`
            }
            target="_blank"
            rel="noreferrer"
          >
            <Image src="/images/salesforce.png" alt="close" w={'100px'} style={{ transform: 'scale(1.35)' }} />
          </a>
        ) : (
          <IdentificationResultIcon boxSize={10} />
        )
      }
      header={`${workshopQuery.data?.name ?? ''} (${workshopQuery.data?.user_readable_id})`}
      contentDescription={t('workshop:description')}
      data-test-id="workshop-detail-card"
    >
      <Stack w="full" data-test-id="workshop-information" spacing={2}>
        {displayRow({
          label: t('workshop:firstname.label'),
          content: get(workshopQuery.data, 'firstname'),
          isLoading: workshopQuery.isLoading,
        })}
        {displayRow({
          label: t('workshop:lastname.label'),
          content: get(workshopQuery.data, 'lastname'),
          isLoading: workshopQuery.isLoading,
        })}
        {displayRow({
          label: t('workshop:email.label'),
          content: get(workshopQuery.data, 'email'),
          isLoading: workshopQuery.isLoading,
        })}
        {displayRow({
          label: t('workshop:telephone.label'),
          content: get(workshopQuery.data, 'telephone'),
          isLoading: workshopQuery.isLoading,
        })}
        {displayRow({
          label: t('workshop:name.label'),
          content: get(workshopQuery.data, 'name'),
          isLoading: workshopQuery.isLoading,
        })}
        <HStack>
          {displayRow({
            label: t('workshop:vat_number.label'),
            content: get(workshopQuery.data, 'vat_number'),
            isLoading: workshopQuery.isLoading,
          })}
          {(workshopQuery.data?.vat_validation_information?.name || workshopQuery.data?.vat_validation_information?.address) && (
            <Tag size={'sm'} variant="solid" bgColor={workshopQuery.data?.vat_validation_information?.valid ? 'green' : 'orange.200'}>
              <Text as={'div'}>{workshopQuery.data?.vat_validation_information?.name}</Text>,
              <Text as={'div'}>{workshopQuery.data?.vat_validation_information?.address}</Text>
            </Tag>
          )}
        </HStack>
        {displayRow({
          label: t('workshop:legal_form.label'),
          content: get(workshopQuery.data, 'legal_form'),
          isLoading: workshopQuery.isLoading,
        })}
        {displayRow({
          label: t('workshop:language.label'),
          content: workshopQuery.data?.language ? t(`location:languages.${workshopQuery.data?.language.toUpperCase()}`) : '-',
          isLoading: workshopQuery.isLoading,
        })}
        {displayRow({
          label: t('workshop:country.label'),
          content: workshopQuery.data?.country ? t(`location:countries.${workshopQuery.data?.country.toUpperCase()}`) : '-',
          isLoading: workshopQuery.isLoading,
        })}
        {displayRow({
          label: t('workshop:city.label'),
          content: get(workshopQuery.data, 'city'),
          isLoading: workshopQuery.isLoading,
        })}
        {displayRow({
          label: t('workshop:zipcode.label'),
          content: get(workshopQuery.data, 'zipcode', '-'),
          isLoading: workshopQuery.isLoading,
        })}
        {displayRow({
          label: t('workshop:address.label'),
          content: get(workshopQuery.data, 'address', '-'),
          isLoading: workshopQuery.isLoading,
        })}
        {displayRow({
          label: t('workshop:registered_at.label'),
          content: dateTimeTransformer(get(workshopQuery.data, 'registered_at')!),
          isLoading: workshopQuery.isLoading,
        })}
        {displayRow({
          label: t('workshop:activated_at.label'),
          content: dateTimeTransformer(get(workshopQuery.data, 'activated_at')!),
          isLoading: workshopQuery.isLoading,
        })}
        {displayRow({
          label: t('workshop:distributor.label'),
          content: get(workshopQuery.data, 'distributor.name') ?? t('workshop:distributor.no_distributor'),
          isLoading: workshopQuery.isLoading,
        })}
        {displayRow({
          label: t('workshop:discovered_us_through.label'),
          content: get(workshopQuery.data, 'discovered_us_through') ?? t('workshop:discovered_us_through.no_data'),
          isLoading: workshopQuery.isLoading,
        })}
      </Stack>
      <Flex direction="row-reverse" py="4" px={{ base: '4', md: '6' }}>
        {isDeletionPending ? (
          <Tooltip label={t('workshop:status.pending_deletion.label')} hasArrow>
            <Button variant="primary" disabled>
              {t('common:edit')}
            </Button>
          </Tooltip>
        ) : (
          <Button variant="primary" as={Link} to={`/app/op/workshops/${workshopQuery.data?.id}/update`}>
            {t('common:edit')}
          </Button>
        )}
      </Flex>
    </ContentCard>
  );
};

export default WorkshopDetailsCard;
